import { config } from '../config';
import { challengeFromVerifier, randomVerifier } from './pkce';

const VERIFIER_KEY = 'axorain.pkce_verifier';

/** redirect_uri must exactly match a Cognito app-client callback URL (incl. the path). */
export const redirectUri = (): string => `${window.location.origin}/auth/callback`;

export interface OAuthTokens {
  readonly id_token: string;
  readonly access_token: string;
  readonly refresh_token: string;
}

/** Kick off the Google login: redirect to the Cognito hosted-UI authorize endpoint. */
export const startGoogleSignIn = async (): Promise<void> => {
  const verifier = randomVerifier();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  const challenge = await challengeFromVerifier(verifier);
  const url = new URL(`https://${config.hostedUiDomain}/oauth2/authorize`);
  url.search = new URLSearchParams({
    response_type: 'code',
    client_id: config.userPoolClientId,
    redirect_uri: redirectUri(),
    scope: 'openid email profile',
    identity_provider: 'Google',
    code_challenge: challenge,
    code_challenge_method: 'S256',
  }).toString();
  window.location.assign(url.toString());
};

/** Exchange the returned code for tokens (public client + PKCE). */
export const exchangeCodeForTokens = async (code: string): Promise<OAuthTokens> => {
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error('Missing PKCE verifier — restart sign-in.');
  const res = await fetch(`https://${config.hostedUiDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.userPoolClientId,
      code,
      redirect_uri: redirectUri(),
      code_verifier: verifier,
    }),
  });
  sessionStorage.removeItem(VERIFIER_KEY);
  if (!res.ok) throw new Error(`Token exchange failed (${res.status}).`);
  return (await res.json()) as OAuthTokens;
};
